import type { PostHog } from "posthog-js";

// ---------------------------------------------------------------------------
// Experiment arms picked at the edge (#529), reported in the browser
// ---------------------------------------------------------------------------
//
// functions/_middleware.js picks the visitor's arm of every experiment running
// on a production page, removes the other arms' blocks from the HTML, and
// writes the assignment at the top of <head>:
//
//   window.__rflktExperiments = { arms: {key: arm}, exposures: [{flag, variant, inView}] }
//
// `arms` is what the page shows (forced arms included); `exposures` is what it
// reports (never a forced arm, never an experiment outside its dates). This
// module reads it and does nothing when it is absent: preview hosts, local
// builds and pages in no experiment.
//
// Reporting follows the playbook (rflkt/.github-private "GENERAL - Experiments",
// section 3): `$feature/<key>` = arm on every event of the page (posthog-js's
// before_send, see gatsby-browser.js), and one `$feature_flag_called` per
// experiment, when its tested block comes within 300 px of the screen for an
// in-view experiment, else once PostHog is up.

export const CONTROL = "control";
/** Same attribute as EXPOSURE_ATTR in functions/_experiments.js. */
export const EXPOSURE_ATTR = "data-experiment-exposure";

export type Exposure = { flag: string; variant: string; inView: boolean };
export type ExperimentState = {
  arms: Record<string, string>;
  exposures: Exposure[];
};

declare global {
  interface Window {
    __rflktExperiments?: ExperimentState;
  }
}

/** The edge's assignment for this document, or undefined. */
export function edgeState(): ExperimentState | undefined {
  if (typeof window === "undefined") return undefined;
  const s = window.__rflktExperiments;
  if (!s || typeof s !== "object" || !s.arms || typeof s.arms !== "object") return undefined;
  return { arms: s.arms, exposures: Array.isArray(s.exposures) ? s.exposures : [] };
}

/** The edge's arm of key, or undefined when the edge picked none. */
export function edgeArm(key: string): string | undefined {
  const arm = edgeState()?.arms[key];
  return typeof arm === "string" ? arm : undefined;
}

/** The arm this visitor sees: the edge's, else control. */
export function experimentArm(key: string): string {
  return edgeArm(key) ?? CONTROL;
}

/**
 * posthog-js before_send hook: adds `$feature/<key>` = arm of every reported
 * experiment to every event (pageview, autocapture, custom events, pageleave).
 * Runs after posthog-js computes its own `$feature/*` properties, so the
 * edge's arm wins over any value PostHog's flag evaluation would add.
 */
export function addExperimentProps<T extends { properties?: Record<string, unknown> } | null>(
  event: T,
): T {
  if (!event) return event;
  const state = edgeState();
  if (!state || state.exposures.length === 0) return event;
  event.properties = event.properties || {};
  for (const e of state.exposures) event.properties[`$feature/${e.flag}`] = e.variant;
  return event;
}

const sent = new Set<string>();
let observers: IntersectionObserver[] = [];

function send(ph: PostHog, e: Exposure) {
  if (sent.has(e.flag)) return;
  sent.add(e.flag);
  ph.capture("$feature_flag_called", {
    $feature_flag: e.flag,
    $feature_flag_response: e.variant,
  });
}

/**
 * Sends this document's exposures, each at most once. Called once PostHog is
 * up (initial = true), then again after each client-side route change
 * (initial = false), since Gatsby re-renders the tested block on every page.
 *
 * An in-view exposure waits for its block (EXPOSURE_ATTR="<key>") to come
 * within 300 px of the screen. On the first call, a page without the block or
 * a browser without IntersectionObserver sends at once, as the Go reference
 * does; on a later route, a page without the block keeps waiting, because
 * the visitor has not seen it there.
 */
export function watchExposures(ph: PostHog, initial: boolean): void {
  const state = edgeState();
  if (!state) return;
  for (const o of observers) o.disconnect();
  observers = [];
  for (const e of state.exposures) {
    if (sent.has(e.flag)) continue;
    if (!e.inView) {
      send(ph, e);
      continue;
    }
    const el = Array.from(document.querySelectorAll(`[${EXPOSURE_ATTR}]`)).find(
      (n) => n.getAttribute(EXPOSURE_ATTR) === e.flag,
    );
    if (!("IntersectionObserver" in window)) {
      if (el || initial) send(ph, e);
      continue;
    }
    if (!el) {
      if (initial) send(ph, e);
      continue;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          io.disconnect();
          send(ph, e);
        }
      },
      { rootMargin: "0px 0px 300px 0px" },
    );
    io.observe(el);
    observers.push(io);
  }
}

/** For tests: forget what was sent and stop watching. */
export function __resetExposures(): void {
  sent.clear();
  for (const o of observers) o.disconnect();
  observers = [];
}
